import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import './FieldGuideWizard.scss';

import { ModalHeader } from '../design-system/components';
import { Overlay } from '../Overlay/Overlay';

import { ApplicationState } from '../../Store';
import {
    FieldGuideConnectionState,
    FieldGuideUploadState,
    GetFieldGuideConnectionState,
    GetFieldGuideHeaderAccountLabel,
    GetFieldGuideUploadState,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { assertNever } from '../../Utils/AssertNever';

import { ConnectingStep } from './ConnectingStep';
import { DestinationStepContainer } from './DestinationStepContainer';

// Matches the Crunchafi.POC ConnectFieldguide modal title, shown by the Connect / ConnectingStep dialog. The
// destination steps have no full-width header: they render inside DestinationFrame, whose header row carries each
// step's heading and the close X, and whose rail carries the "Send to Fieldguide" logo.
const CONNECT_TITLE = 'Connect to Fieldguide';

type InjectedReduxState = {
    // The "Connected as {email}" / "Connected to FieldGuide" label, shown under the Connect dialog's header while the
    // connection is Connected (the destination steps show the account in their rail instead); undefined otherwise.
    headerAccountLabel: string | undefined;
    // True only when connected and an upload has begun: the destination steps render once ConnectingStep's
    // precheck is ready, and they dispatch BeginUpload on mount. Selects the dialog chrome and width.
    showDestination?: boolean;
    // Sizes the destination frame: the client and engagement steps use the POC's narrow sidebar modal.
    uploadState?: FieldGuideUploadState;
};

type InjectedActionCreators = {
    // Optional so the connect-only tests can render the pure component without it.
    CancelUpload?: () => void;
};

type FieldGuideWizardProps = {
    onClose: () => void;
};

type Props = InjectedReduxState & InjectedActionCreators & FieldGuideWizardProps;

/**
 * The POC's per-screen width: MODAL_WIDTH_WITH_SIDEBAR_NARROW (880px) while picking the client or engagement (and
 * before the upload has begun, which shows the client step), MODAL_WIDTH_WITH_SIDEBAR (1440px) from the request step on.
 */
function isNarrowFrame(uploadState: FieldGuideUploadState | undefined): boolean {
    switch (uploadState) {
        case undefined:
        case FieldGuideUploadState.SelectingCompany:
        case FieldGuideUploadState.SelectingEngagement:
            return true;
        case FieldGuideUploadState.MappingRequests:
        case FieldGuideUploadState.Reviewing:
        case FieldGuideUploadState.Sending:
        case FieldGuideUploadState.Succeeded:
        case FieldGuideUploadState.Failed:
            return false;
        default:
            return assertNever(uploadState);
    }
}

function dialogClassName(showDestination: boolean | undefined, uploadState: FieldGuideUploadState | undefined): string {
    if (!showDestination) {
        return 'cr-modal field-guide-wizard';
    }

    return `cr-modal field-guide-wizard field-guide-wizard--destination${isNarrowFrame(uploadState) ? ' field-guide-wizard--narrow' : ''}`;
}

export const FieldGuideWizardComponent: React.FC<Props> = (props): React.ReactElement => {
    const { headerAccountLabel, showDestination, uploadState, CancelUpload, onClose } = props;

    // The wizard is mounted conditionally, so a route change unmounts it without any close action; the
    // upload selection must still be reset or it leaks into the next collection's wizard. Lives here
    // (not in DestinationStep) so it survives the reconnect round trip where only DestinationStep unmounts.
    const cancelUploadRef = React.useRef(CancelUpload);
    cancelUploadRef.current = CancelUpload;
    React.useEffect(() => (): void => cancelUploadRef.current?.(), []);

    const close = (): void => {
        if (CancelUpload) {
            CancelUpload();
        }
        onClose();
    };

    // One tree for both chromes: ConnectingStep (and the DestinationStepContainer it hands off to) keeps its position
    // when showDestination flips, so the precheck does not re-run and the destination steps mount exactly once.
    return (
        <Overlay onOverlayClick={close}>
            <div className={dialogClassName(showDestination, uploadState)}>
                {!showDestination && <ModalHeader title={CONNECT_TITLE} onClose={close} />}
                {!showDestination && headerAccountLabel && (
                    <div className={'field-guide-wizard__account page-help-text'}>{headerAccountLabel}</div>
                )}
                <div className={showDestination ? 'field-guide-wizard__destination-body' : 'cr-modal-body field-guide-wizard__body'}>
                    <ConnectingStep connectedContent={<DestinationStepContainer onCancel={close} />} />
                </div>
            </div>
        </Overlay>
    );
};

export const FieldGuideWizard = connect<InjectedReduxState, InjectedActionCreators, FieldGuideWizardProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            headerAccountLabel: GetFieldGuideHeaderAccountLabel(appState),
            showDestination: GetFieldGuideConnectionState(appState) === FieldGuideConnectionState.Connected
                && GetFieldGuideUploadState(appState) !== undefined,
            uploadState: GetFieldGuideUploadState(appState),
        };
        return result;
    },
    {
        CancelUpload: FieldGuideStoreActions.CancelUpload,
    }
)(FieldGuideWizardComponent);
